/*!
 * Roundway Quiz Engine
 * Shared renderer for popup + PDP funnel quiz.
 *
 * Two scoring modes:
 *   - legacy persona model (config.personas, no tagVector flag)
 *   - tag-vector model (config.tagVector === true + window.RoundwayQuizProducts)
 *
 * Tag-vector flow:
 *   - Each answer option carries `weights: { tag: n }` over the tag dictionary.
 *   - Final score per product = sum_tag(answer_vec[tag] * product.tags[tag]).
 *   - Modifiers and stage are normal answers, they just push specific tags.
 *   - Result = top-1 primary + top-2-of-different-category stack suggestion.
 *
 * Branching:
 *   - Questions can declare `showIf(answers)` and are skipped when false.
 *   - Questions can declare `optionsByAnswer: { qid: { value: [options] } }`
 *     which swaps the options list based on a prior answer.
 *   - Progress bar uses the *active* path length, recomputed each render.
 *
 * PDP seeding:
 *   - Attribute `product-handle` is mapped via RoundwayQuizHandleMap. If found,
 *     Q1 is pre-answered (and skipped) and the quiz is anchored to that product
 *     unless the user's other answers push the score >20% above it.
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('roundway-quiz')) return;

  // Bump the dismiss key so stale localStorage from the broken popup rollout
  // does not keep hiding the fixed teaser for returning visitors/test sessions.
  const DISMISS_KEY_PREFIX = 'rwq_dismiss_v2_';
  const SUBMIT_KEY_PREFIX  = 'rwq_submit_';

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'rwq-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  function valid(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim()); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  // -------- Lifestyle image cache --------
  // Product packshots are small; Shopify metafield lifestyle images are the
  // better visual for the result card. Resolved lazily per handle.
  const LIFESTYLE_CACHE = {};

  // -------- Scoring --------

  // Build the summed tag-vector from current answers + the active question list.
  function buildTagVector(activeQuestions, answers) {
    const vec = {};
    activeQuestions.forEach(q => {
      const a = answers[q.id];
      const picks = Array.isArray(a) ? a : (a == null ? [] : [a]);
      const opts = resolveOptions(q, answers);
      picks.forEach(v => {
        const opt = opts.find(o => o.v === v);
        if (!opt || !opt.weights) return;
        Object.keys(opt.weights).forEach(k => { vec[k] = (vec[k] || 0) + opt.weights[k]; });
      });
    });
    return vec;
  }

  function scoreProducts(answerVec, products) {
    const results = [];
    const wantsKids = !!answerVec.kids;
    Object.keys(products).forEach(key => {
      const p = products[key];
      // Hard gate: kids-only SKUs must not surface unless the user signalled kids.
      if (p.category === 'kids' && !wantsKids) return;
      let score = 0;
      Object.keys(p.tags || {}).forEach(t => {
        if (answerVec[t]) score += answerVec[t] * p.tags[t];
      });
      results.push({ key, product: p, score });
    });
    results.sort((a, b) => b.score - a.score);
    return results;
  }

  function pickStack(primary, ranked) {
    if (!primary) return null;
    for (let i = 0; i < ranked.length; i++) {
      const r = ranked[i];
      if (r.key === primary.key) continue;
      if (r.product.category === primary.product.category) continue;
      // Guard: never pair two SKUs that resolve to the same Shopify product
      // (e.g. cleanse_bundle and wormwood_binder share the same handle).
      if (r.product.handle && primary.product.handle && r.product.handle === primary.product.handle) continue;
      if (r.product.url && primary.product.url && r.product.url === primary.product.url) continue;
      if (r.score <= 0) return null;
      return r;
    }
    return null;
  }

  function topTags(answerVec, n) {
    return Object.keys(answerVec)
      .sort((a, b) => answerVec[b] - answerVec[a])
      .filter(k => answerVec[k] > 0)
      .slice(0, n || 3);
  }

  // -------- Question helpers --------

  function resolveOptions(q, answers) {
    if (q.optionsByAnswer) {
      // first matching dependency wins
      const deps = Object.keys(q.optionsByAnswer);
      for (let i = 0; i < deps.length; i++) {
        const dep = deps[i];
        const v = answers[dep];
        if (v && q.optionsByAnswer[dep] && q.optionsByAnswer[dep][v]) {
          return q.optionsByAnswer[dep][v];
        }
      }
    }
    return q.options || [];
  }

  function activePath(config, answers) {
    return config.questions.filter(q => {
      if (typeof q.showIf === 'function') return !!q.showIf(answers);
      return true;
    });
  }

  // -------- Legacy persona scoring (kept for back-compat configs) --------
  function scorePersona(config, answers) {
    const scores = {};
    config.questions.forEach(q => {
      const a = answers[q.id];
      const picks = Array.isArray(a) ? a : (a == null ? [] : [a]);
      picks.forEach(v => {
        const opt = (q.options || []).find(o => o.v === v);
        if (!opt || !opt.weights) return;
        Object.keys(opt.weights).forEach(k => { scores[k] = (scores[k] || 0) + opt.weights[k]; });
      });
    });
    let best = null, bestScore = -Infinity;
    Object.keys(scores).forEach(k => { if (scores[k] > bestScore) { best = k; bestScore = scores[k]; } });
    return best || config.defaultPersona || Object.keys(config.personas || {})[0];
  }

  class RoundwayQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = {
        open: false, teaser: false, step: 0, answers: {}, email: '',
        submitting: false, persona: null, primary: null, stack: null,
        runnerUp: null, pathTaken: [], topTags: []
      };
    }

    connectedCallback() {
      if (this._rwqInitialized) return;
      this.configId = this.getAttribute('config-id') || 'funnel';
      this.mode     = this.getAttribute('mode') || 'inline';

      // Sitewide modal quiz is retired. Keep the engine available for inline
      // quiz page/PDP embeds only, and make any legacy modal mount inert.
      if (this.mode === 'modal' || (window.RoundwayQuizPopupDisabled && this.mode !== 'inline')) {
        this.innerHTML = '';
        this.style.display = 'none';
        this.setAttribute('aria-hidden', 'true');
        try { this.remove(); } catch (_) {}
        this._rwqInitialized = true;
        return;
      }

      this.apiBase  = this.getAttribute('api-base') || '';
      this.anonKey  = this.getAttribute('anon-key') || '';
      this.shopify  = this.getAttribute('shopify-domain') || (location && location.hostname) || '';
      this.custEmail= this.getAttribute('customer-email') || '';
      this.custId   = this.getAttribute('customer-id') || '';
      this.pageHandle = this.getAttribute('page-handle') || '';
      this.productHandle = this.getAttribute('product-handle') || '';

      const cfgs = window.RoundwayQuizConfigs || {};
      this.config = cfgs[this.configId];
      if (!this.config) {
        this._rwqConfigAttempts = (this._rwqConfigAttempts || 0) + 1;
        if (this._rwqConfigAttempts <= 40) {
          setTimeout(() => this.connectedCallback(), 75);
        } else {
          this.innerHTML = '';
          console.warn('[rwq] missing config', this.configId);
        }
        return;
      }
      this._rwqInitialized = true;

      // Resolve products registry (tag-vector mode only)
      if (this.config.tagVector) {
        this.products = window[this.config.productsKey || 'RoundwayQuizProducts'] || {};
        this.handleMap = window[this.config.handleMapKey || 'RoundwayQuizHandleMap'] || {};
        this.anchorKey = this.handleMap[this.productHandle] || null;
        // Seed Q1=goal from the anchor product when on PDP.
        if (this.anchorKey && this.products[this.anchorKey]) {
          const seededGoal = this.inferGoalFromProduct(this.anchorKey);
          if (seededGoal) this.state.answers.goal = seededGoal;
        }
      }

      if (this.custEmail) this.state.email = this.custEmail;
      this.sessionId = uuid();
      this.classList.add('rwq-root', 'rwq-mode-' + this.mode);

      if (this.mode === 'modal') {
        // Teaser is ALWAYS present bottom-left. Dismiss/submit only suppress the
        // auto-opened modal, never the teaser itself — visitors must always have
        // a click target to re-open the quiz.
        const forceQuiz = decodeURIComponent((location.search.match(/[?&]rw_force_format=([^&]+)/) || [])[1] || '') === 'quiz';
        let suppressAutoOpen = false;
        try {
          if (!forceQuiz && localStorage.getItem(SUBMIT_KEY_PREFIX + this.configId)) suppressAutoOpen = true;
          if (!forceQuiz && localStorage.getItem('rw_popup_submitted')) suppressAutoOpen = true;
          const d = Number(localStorage.getItem(DISMISS_KEY_PREFIX + this.configId) || 0);
          if (!forceQuiz && d && Date.now() - d < 1000 * 60 * 60 * 24 * 7) suppressAutoOpen = true;
        } catch (_) {}
        this._suppressAutoOpen = suppressAutoOpen;
        // Show teaser immediately regardless of bandit / dismiss / submit state.
        this.showTeaser();
        this.awaitBandit();
      } else {
        this.state.open = true;
        this.render();
      }
    }

    // Map a product key to the closest Q1 goal answer so PDP entry can skip Q1.
    inferGoalFromProduct(key) {
      const p = this.products[key]; if (!p) return null;
      const t = p.tags || {};
      if (t.kids) return 'kids';
      if (t.sleep || t.stress) return 'sleep';
      if (t.liver || t.energy) return 'energy';
      if (t.immunity || t.recovery) return 'immunity';
      if (t.hormones || t.skin) return 'hormones';
      return 'gut';
    }

    awaitBandit() {
      if (this.mode === 'modal' || window.RoundwayQuizPopupDisabled) return;
      const self = this;
      const force = (location.search.match(/[?&]rw_force_format=([^&]+)/) || [])[1];
      const bandit = window.RoundwayBandit;
      if (!bandit || !bandit.ready) {
        this._rwqBanditAttempts = (this._rwqBanditAttempts || 0) + 1;
        if (this._rwqBanditAttempts <= 40) {
          setTimeout(() => self.awaitBandit(), 75);
          return;
        }
        // Fail open. The legacy popup is suppressed on the live theme, so a
        // missing bandit must not leave visitors with no teaser and no capture.
        self.showTeaser();
        if (!self._suppressAutoOpen) setTimeout(() => self.open('fallback'), self.config.delayMs || 6000);
        return;
      }
      bandit.ready.then(function (res) {
        const fmt = (res && res.format) || 'standard';
        self.impressionId = res.impressionId || null;
        self.sessionId = res.sessionId || self.sessionId;
        const chosen = (res && res.chosen) || {};
        self.banditChosen = chosen;
        if (fmt === 'quiz' || force === 'quiz') self.applyBanditStory(chosen);
        // Teaser is ALWAYS visible. Bandit suppress / prior submit / prior
        // dismiss only skip the auto-opened modal, never the teaser.
        self.showTeaser();
        const skipAutoOpen = (res && res.suppress && force !== 'quiz') || self._suppressAutoOpen;
        if (!skipAutoOpen) setTimeout(() => self.open('auto'), self.config.delayMs || 6000);
      });
    }

    showTeaser() {
      if (this.mode === 'modal' || window.RoundwayQuizPopupDisabled) return;
      if (this.state.open || this.state.teaser) return;
      this.state.teaser = true;
      this.render();
      this.trackViewOnce('teaser');
      this.quizEvent('teaser_view', { step_index: 0, payload: { teaser: true } });
    }

    applyBanditStory(chosen) {
      const s = Object.assign({}, this.config.story || {});
      if (chosen.headline && chosen.headline.value) s.title = chosen.headline.value;
      if (chosen.sub && chosen.sub.value) s.sub = chosen.sub.value;
      if (chosen.image && chosen.image.value) s.image = chosen.image.value;
      if (chosen.eyebrow && chosen.eyebrow.value) s.eyebrow = chosen.eyebrow.value;
      this.config.story = s;
      if (chosen.offer_reveal && chosen.offer_reveal.value) {
        this.config.defaultCode = chosen.offer_reveal.value;
      }
    }

    open(source) {
      this.state.open = true;
      this.state.teaser = false;
      // If Q1 was seeded from PDP, jump past it.
      if (this.anchorKey && this.state.answers.goal) {
        this.state.step = this.nextStepIndex(0);
      }
      this.render();
      this.trackViewOnce(source || 'auto');
      this.quizEvent('step_view', { step_index: typeof this.state.step === 'number' ? this.state.step : 0 });
    }

    trackViewOnce(source) {
      if (this._rwqViewTracked) return;
      this._rwqViewTracked = true;
      const stepIdx = typeof this.state.step === 'number' ? this.state.step : 0;
      this.event('view', { step: stepIdx, anchored: !!this.anchorKey, source: source || 'auto' });
      this.quizEvent('view', { step_index: stepIdx, payload: { source: source || 'auto' } });
    }

    close(reason) {
      const wasOpen = this.state.open;
      this.state.open = false;
      // Teaser is persistent — it must remain visible after every close so
      // visitors can always re-open the quiz. Record the dismiss timestamp
      // only to suppress the next auto-open, never to hide the teaser.
      if (reason === 'teaser-dismiss' || reason === 'close' || reason === 'backdrop') {
        try { localStorage.setItem(DISMISS_KEY_PREFIX + this.configId, String(Date.now())); } catch (_) {}
      }
      this.state.teaser = true;
      this._rwqViewTracked = false;
      this.render();
      this.event('dismiss', { reason: reason || 'close' });
      this.quizEvent('dismiss', { payload: { reason: reason || 'close', from_open: wasOpen } });
    }

    event(type, payload) {
      if (!this.apiBase) return;
      const isModal = this.mode === 'modal';
      try {
        fetch(this.apiBase + '/popup-event', {
          method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
          body: JSON.stringify({
            session_id: this.sessionId,
            popup_id: isModal ? 'default' : ('quiz-' + this.configId),
            impression_id: this.impressionId || null,
            event_type: type,
            popup_format: isModal ? 'quiz' : 'pdp_quiz',
            page: location.pathname,
            page_handle: this.pageHandle,
            shopify_domain: this.shopify,
            customer_id: this.custId || null,
            payload: Object.assign({
              quiz: this.configId,
              path_taken: this.state.pathTaken,
              recommended_product: this.state.primary ? this.state.primary.key : null
            }, payload || {})
          })
        }).catch(() => {});
      } catch (_) {}
    }

    // Step-level analytics → quiz_events table (separate from popup_events).
    quizEvent(type, fields) {
      if (!this.apiBase) return;
      const isModal = this.mode === 'modal';
      const f = fields || {};
      const stepIdx = (typeof f.step_index === 'number')
        ? f.step_index
        : (typeof this.state.step === 'number' ? this.state.step : null);
      const cfg = this.config || {};
      const qs = Array.isArray(cfg.questions) ? cfg.questions : [];
      const stepKey = f.step_key
        || (typeof stepIdx === 'number' && qs[stepIdx] ? qs[stepIdx].id : (typeof this.state.step === 'string' ? this.state.step : null));
      try {
        fetch(this.apiBase + '/quiz-event', {
          method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
          body: JSON.stringify({
            session_id: this.sessionId,
            quiz_id: this.configId,
            popup_id: isModal ? 'default' : ('quiz-' + this.configId),
            popup_format: isModal ? 'quiz' : 'pdp_quiz',
            event_type: type,
            step_index: stepIdx,
            step_key: stepKey,
            question_id: f.question_id || null,
            answer_value: f.answer_value != null ? String(f.answer_value).slice(0, 200) : null,
            page: location.pathname,
            page_handle: this.pageHandle,
            shopify_domain: this.shopify,
            customer_id: this.custId || null,
            email: f.email || null,
            payload: Object.assign({
              path_taken: this.state.pathTaken,
              recommended_product: this.state.primary ? this.state.primary.key : null,
              total_steps: qs.length
            }, f.payload || {})
          })
        }).catch(() => {});
      } catch (_) {}
    }

    // Branching: find next visible question after `from` (inclusive).
    nextStepIndex(from) {
      const answers = this.state.answers;
      const qs = this.config.questions;
      for (let i = from; i < qs.length; i++) {
        const q = qs[i];
        if (typeof q.showIf === 'function' && !q.showIf(answers)) continue;
        return i;
      }
      return 'results';
    }
    prevStepIndex(from) {
      const answers = this.state.answers;
      const qs = this.config.questions;
      for (let i = from; i >= 0; i--) {
        const q = qs[i];
        if (typeof q.showIf === 'function' && !q.showIf(answers)) continue;
        return i;
      }
      return 0;
    }

    pick(qId, value) {
      const q = this.config.questions.find(x => x.id === qId);
      if (!q) return;
      const fromStep = typeof this.state.step === 'number' ? this.state.step : null;
      if (q.multi) {
        const cur = Array.isArray(this.state.answers[qId]) ? this.state.answers[qId].slice() : [];
        const i = cur.indexOf(value);
        if (i >= 0) cur.splice(i, 1); else cur.push(value);
        this.state.answers[qId] = cur;
        this.quizEvent('q_answer', { question_id: qId, answer_value: cur.join(','), step_index: fromStep });
        // In-place toggle to avoid full re-render flicker between pick and advance.
        try {
          const btn = this.querySelector('[data-rwq-pick="' + qId + '|' + value + '"]');
          if (btn) btn.classList.toggle('is-on', cur.indexOf(value) >= 0);
        } catch (_) {}
        const nxt = this.nextStepIndex((this.state.step || 0) + 1);
        if (this._multiTimer) clearTimeout(this._multiTimer);
        if (cur.length > 0) {
          this._multiTimer = setTimeout(() => {
            this._multiTimer = null;
            if (nxt === 'results') this.toResults();
            else { this.state.step = nxt; this.render(); this.quizEvent('step_view', { step_index: nxt }); }
          }, 700);
        }
      } else {
        this.state.answers[qId] = value;
        this.event('q_answer', { q: qId, v: value, step: this.state.step });
        this.quizEvent('q_answer', { question_id: qId, answer_value: value, step_index: fromStep });
        const nxt = this.nextStepIndex((this.state.step || 0) + 1);
        if (nxt === 'results') { this.toResults(); }
        else { this.state.step = nxt; setTimeout(() => { this.render(); this.quizEvent('step_view', { step_index: nxt }); }, 140); }
      }
    }

    next() {
      const nxt = this.nextStepIndex((this.state.step || 0) + 1);
      if (nxt === 'results') this.toResults();
      else { this.state.step = nxt; this.render(); }
    }

    back() {
      if (typeof this.state.step !== 'number' || this.state.step <= 0) return;
      const prev = this.prevStepIndex(this.state.step - 1);
      this.state.step = prev;
      this.render();
    }

    toResults() {
      const active = activePath(this.config, this.state.answers);
      this.state.pathTaken = active.map(q => q.id);

      if (this.config.tagVector) {
        const vec = buildTagVector(active, this.state.answers);
        const ranked = scoreProducts(vec, this.products);
        this.state.topTags = topTags(vec, 4);

        let primary = ranked[0] || null;
        // Anchor preference: if a PDP product scores within 20% of the top, prefer it.
        if (this.anchorKey) {
          const anchored = ranked.find(r => r.key === this.anchorKey);
          if (anchored && primary && anchored.score >= primary.score * 0.8) {
            primary = anchored;
          }
        }
        const stack = pickStack(primary, ranked);
        this.state.primary = primary;
        this.state.stack = stack;
        this.state.runnerUp = ranked.find(r => primary && r.key !== primary.key && (!stack || r.key !== stack.key)) || null;
        this.state.persona = primary ? primary.key : null;
      } else {
        this.state.persona = scorePersona(this.config, this.state.answers);
      }

      this.state.step = 'results';
      this.event('results', {
        persona: this.state.persona,
        primary: this.state.primary && this.state.primary.key,
        stack:   this.state.stack && this.state.stack.key,
        answers: this.state.answers
      });
      // Email gate is shown on the results step before plan reveal.
      this.quizEvent('gate_view', {
        step_key: 'gate',
        payload: {
          persona: this.state.persona,
          primary: this.state.primary && this.state.primary.key,
          stack: this.state.stack && this.state.stack.key
        }
      });
      this.render();
    }

    async submitEmail() {
      const email = (this.state.email || '').trim().toLowerCase();
      if (!valid(email)) { alert("That email doesn't look right."); return; }
      this.state.submitting = true; this.render();
      try {
        const code = this.config.defaultCode || 'n10';
        const primary = this.state.primary;
        const stack   = this.state.stack;
        const draftStack = [];
        if (primary && primary.product && primary.product.handle) {
          draftStack.push({ handle: primary.product.handle, score: primary.score, category: primary.product.category, reason: this.buildReasoning ? this.buildReasoning() : null });
        }
        if (stack && stack.product && stack.product.handle) {
          draftStack.push({ handle: stack.product.handle, score: stack.score, category: stack.product.category, reason: 'Pairs with your primary match.' });
        }
        const submitP = fetch(this.apiBase + '/quiz-submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
          body: JSON.stringify({
            product: this.config.product,
            variant: this.config.variant || 'v1',
            persona: this.state.persona,
            answers: this.state.answers,
            email: email,
            shopify_customer_id: this.custId || null,
            shopify_domain: this.shopify,
            page_handle: this.pageHandle,
            session_id: this.sessionId,
            discount_code: code,
            recommended_product: primary ? primary.key : null,
            stack_product: stack ? stack.key : null,
            top_tags: this.state.topTags || [],
            path_taken: this.state.pathTaken || [],
            utm: { source: 'quiz', medium: 'pdp', campaign: this.config.product, content: this.state.persona }
          })
        });
        const a = this.state.answers || {};
        const recommendP = fetch(this.apiBase + '/quiz-recommend', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
          body: JSON.stringify({
            anon_id: this.sessionId,
            email: email,
            shopify_domain: this.shopify,
            intake: {
              answers: a,
              age_band: a.age || a.age_band,
              pregnancy: a.pregnancy,
              allergens: Array.isArray(a.allergens) ? a.allergens : (a.allergens ? [a.allergens] : []),
              diet: a.diet,
              format: a.format,
              wake_time: a.wake_time,
              bed_time: a.bed_time,
              caffeine_cutoff: a.caffeine_cutoff,
              goal: a.goal
            },
            draft_stack: draftStack,
            draft_pairings: [],
            discount_code: code,
            utm: { utm_source: 'quiz', utm_medium: this.mode === 'modal' ? 'popup' : 'pdp', utm_campaign: this.config.product, utm_content: this.state.persona }
          })
        }).then(r => r.ok ? r.json() : null).then(j => {
          if (j && j.plan) this.state.serverPlan = j.plan;
          if (j && j.blocked) this.state.serverBlocked = j;
        }).catch(e => console.warn('[rwq] recommend failed', e));
        await Promise.all([submitP, recommendP]);
        try {
          localStorage.setItem(SUBMIT_KEY_PREFIX + this.configId, '1');
          localStorage.setItem('rw_popup_submitted', String(Date.now()));
        } catch (_) {}
        this.event('submit', { persona: this.state.persona });
        this.quizEvent('email_submit', { step_key: 'gate', email, payload: { persona: this.state.persona } });
        this.state.step = 'thanks';
        this.quizEvent('results_view', { step_key: 'thanks' });
      } catch (e) {
        console.error('[rwq] submit failed', e);
        this.event('submit_error', { msg: String(e && e.message || e) });
      }
      this.state.submitting = false;
      this.render();
    }

    render() {
      // Teaser in modal mode is PERSISTENT — always in DOM, regardless of
      // session, submit, dismiss, cooldown, or whether the modal is open.
      const teaserHTML = this.mode === 'modal' ? this.teaserMarkup() : '';

      if (this.mode === 'modal' && !this.state.open) {
        this.innerHTML = teaserHTML;
        this.bind();
        return;
      }
      const cfg = this.config;
      let body;
      if (this.state.step === 'results') body = this.renderResults();
      else if (this.state.step === 'thanks') body = this.renderThanks();
      else body = this.renderQuestion(cfg.questions[this.state.step]);

      // Progress against active path
      const active = activePath(cfg, this.state.answers);
      const totalQs = active.length;
      const isResults = this.state.step === 'results' || this.state.step === 'thanks';
      let curIdx;
      if (isResults) { curIdx = totalQs; }
      else {
        const q = cfg.questions[this.state.step];
        const ai = active.findIndex(x => x.id === q.id);
        curIdx = ai < 0 ? 0 : ai;
      }
      const pct = Math.max(8, Math.round(((curIdx + (isResults ? 0 : 1)) / Math.max(1, totalQs)) * 100));
      const stepLabel = this.state.step === 'thanks'
        ? 'Complete'
        : this.state.step === 'results'
          ? 'Your match'
          : 'Step ' + (curIdx + 1) + ' / ' + totalQs;

      const s = cfg.story || {};
      const heroImg = s.image || cfg.heroImage || '';
      const heroDiv = '<div class="rwq-hero"' +
        (heroImg ? ' style="background-image:url(' + JSON.stringify(heroImg).slice(1, -1) + ')"' : '') +
        '></div>';

      const canBack = (!isResults && typeof this.state.step === 'number' && this.prevStepIndex(this.state.step - 1) !== this.state.step && this.state.step > 0);
      const back = canBack ? '<button class="rwq-back" data-rwq-back>← Back</button>' : '';
      const trust = s.foot
        ? '<span class="rwq-trust"><span class="rwq-trust-stars">★★★★★</span><span>' + esc(s.foot) + '</span></span>'
        : '';
      const footInner = (back || trust)
        ? (back + (back && trust ? '<span style="opacity:.4">·</span>' : '') + trust)
        : '';

      const themeAttr = cfg.theme ? ' data-rwq-theme="' + esc(cfg.theme) + '"' : '';
      const wordmark = cfg.theme === 'wellgard'
        ? '<div class="rwq-wordmark"><span class="rwq-wordmark-text">ROUNDWAY</span><span class="rwq-wordmark-sub">— Supplements —</span></div>'
        : '';
      const card =
        '<div class="rwq-card' + (heroImg ? '' : ' no-image') + '"' + themeAttr + ' role="dialog" aria-modal="true">' +
          heroDiv +
          (this.mode === 'modal' ? '<button class="rwq-close" data-rwq-close aria-label="Close">×</button>' : '') +
          '<div class="rwq-content">' +
            '<div class="rwq-top">' +
              '<div class="rwq-meta">' +
                '<span>' + esc(stepLabel) + '</span>' +
                '<span class="rwq-meta-bar"><span class="rwq-meta-bar-fill" style="width:' + pct + '%"></span></span>' +
              '</div>' +
            '</div>' +
            '<div class="rwq-fadein">' + body + '</div>' +
            (footInner ? '<div class="rwq-foot-row">' + footInner + '</div>' : '<div></div>') +
            wordmark +
          '</div>' +
        '</div>';

      this.innerHTML = (this.mode === 'modal'
        ? (this.state.open ? '' : teaserHTML) + '<div class="rwq-backdrop" data-rwq-backdrop></div>' + card
        : card);
      this.bind();
      this.upgradeResultImage();
    }

    // Swap the small packshot on the results card for the product's Shopify
    // metafield lifestyle image (custom.lifestyle_image_1..5) when available.
    async upgradeResultImage() {
      const img = this.querySelector('[data-rwq-result-img]');
      if (!img) return;
      const handle = img.getAttribute('data-rwq-handle');
      if (!handle || !this.apiBase) return;
      try {
        if (!(handle in LIFESTYLE_CACHE)) {
          LIFESTYLE_CACHE[handle] = fetch(this.apiBase + '/shopify-fetch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
            body: JSON.stringify({ resource: 'product', handle: handle })
          }).then(r => (r.ok ? r.json() : null)).then(j => {
            const ls = j && j.product && Array.isArray(j.product.lifestyleImages) ? j.product.lifestyleImages : [];
            return ls[0] || null;
          }).catch(() => null);
        }
        const src = await LIFESTYLE_CACHE[handle];
        if (!src) return;
        // Element may have been re-rendered while fetching — re-query.
        const live = this.querySelector('[data-rwq-result-img]');
        if (live && live.getAttribute('data-rwq-handle') === handle) {
          live.src = src;
          live.classList.add('rwq-result-img--lifestyle');
        }
      } catch (_) { /* keep packshot */ }
    }

    teaserMarkup() {
      const s = this.config.story || {};
      const title = s.teaserShort || 'Find your gut match';
      const kicker = s.teaserKicker || 'Exclusive discount · 60 seconds';
      return (
        '<button class="rwq-teaser" type="button" data-rwq-teaser aria-label="Open Roundway quiz">' +
          '<span class="rwq-teaser-icon" aria-hidden="true">→</span>' +
          '<span class="rwq-teaser-copy">' +
            '<span class="rwq-teaser-kicker">' + esc(kicker) + '</span>' +
            '<span class="rwq-teaser-title">' + esc(title) + '</span>' +
          '</span>' +
        '</button>'
      );
    }

    renderTeaser() { this.innerHTML = this.teaserMarkup(); this.bind(); }

    renderQuestion(q) {
      const selected = this.state.answers[q.id];
      const s = this.config.story || {};
      const eyebrow = s.eyebrow || this.config.eyebrow || 'Roundway · 60-second match';
      const opts = resolveOptions(q, this.state.answers);
      const layout = q.layout || 'pills';
      const tilesCls = 'rwq-tiles rwq-layout-' + layout;
      const tiles = opts.map(o => {
        const isOn = q.multi ? (Array.isArray(selected) && selected.indexOf(o.v) >= 0) : selected === o.v;
        const iconRaw = o.icon || '';
        const iconHtml = iconRaw ? (/^\s*<svg/i.test(iconRaw) ? iconRaw : esc(iconRaw)) : '';
        const icon = iconHtml ? '<span class="rwq-tile-icon">' + iconHtml + '</span>' : '';
        return '<button class="rwq-tile' + (isOn ? ' is-on' : '') + '" data-rwq-pick="' + esc(q.id) + '|' + esc(o.v) + '">' +
                 icon +
                 '<span class="rwq-tile-text">' +
                   '<span class="rwq-tile-label">' + esc(o.label) + '</span>' +
                   (o.hint ? '<span class="rwq-tile-hint">' + esc(o.hint) + '</span>' : '') +
                 '</span>' +
                 (layout === 'pills' ? '<span class="rwq-tile-arrow">→</span>' : '') +
               '</button>';
      }).join('');
      const next = q.multi
        ? '<p class="rwq-hint" style="font-size:11px;opacity:.65;margin-top:2px;">Pick any that apply</p>'
        : '';
      const titleHtml = esc(q.title).replace(/_([^_]+)_/g, '<em>$1</em>');
      const rewardCode = this.config.defaultCode || '';
      const rewardPct  = this.config.rewardPct || '10%';
      const reward = rewardCode && layout !== 'circles'
        ? '<div class="rwq-reward"><span class="rwq-reward-gift">✦</span><span class="rwq-reward-text"><strong>' + esc(rewardPct) + ' off</strong> on us, plus a free 3-in-1 Magnesium pack on every order over £25.</span></div>'
        : '';
      const banner = q.banner
        ? '<p class="rwq-answer-banner">Answer for an <em>exclusive discount</em> tailored to your needs.</p>'
        : '';
      return (
        banner +
        '<h3 class="rwq-title">' + titleHtml + '</h3>' +
        (q.hint ? '<p class="rwq-hint">' + esc(q.hint) + '</p>' : '') +
        '<div class="' + tilesCls + '">' + tiles + '</div>' +
        reward +
        next
      );
    }

    // ----- Tag-vector result card -----
    renderResults() {
      if (!this.config.tagVector) return this.renderLegacyResults();
      const primary = this.state.primary;
      const stack = this.state.stack;
      const code = this.config.defaultCode || 'n10';
      const pct  = this.config.rewardPct || '10%';

      if (!primary) {
        return '<h3 class="rwq-title">Hmm, no clear match.</h3>' +
               '<p class="rwq-hint">Try a couple of different answers.</p>' +
               '<div class="rwq-cta-row"><button class="rwq-cta" data-rwq-back>← Back</button></div>';
      }

      const why = this.buildReasoning();

      // Locked teaser: show the match name + image + reasoning, but NO shop CTAs.
      // The only way forward is the email form. CTAs unlock on the thanks step.
      const teaser =
        '<div class="rwq-result-card rwq-locked">' +
          (primary.product.image
            ? '<img class="rwq-result-img" data-rwq-result-img src="' + esc(primary.product.image) + '" alt=""' +
              (primary.product.handle ? ' data-rwq-handle="' + esc(primary.product.handle) + '"' : '') +
              ' onerror="this.style.display=\'none\'" />'
            : '') +
          '<h3 class="rwq-title">' + esc(primary.product.name) + '</h3>' +
          (primary.product.blurb ? '<p class="rwq-hint">' + esc(primary.product.blurb) + '</p>' : '') +
          (why ? '<p class="rwq-hint rwq-why"><em>Why:</em> ' + esc(why) + '</p>' : '') +
          (stack ? '<p class="rwq-hint rwq-pair">Also worth adding: <strong>' + esc(stack.product.name) + '</strong></p>' : '') +
        '</div>';

      // Big, prominent offer + email gate.
      const offer =
        '<div class="rwq-offer">' +
          '<span class="rwq-offer-pill">Held for you</span>' +
          '<div class="rwq-offer-headline"><span class="rwq-offer-pct">' + esc(pct) + ' off</span><span class="rwq-offer-on-us">on us</span></div>' +
          '<p class="rwq-offer-sub">Plus a free 3-in-1 Magnesium pack on every order over £25.</p>' +
          '<div class="rwq-email-row">' +
            '<input type="email" class="rwq-input" data-rwq-email placeholder="you@yours.com" value="' + esc(this.state.email) + '" />' +
            '<button class="rwq-cta" data-rwq-submit ' + (this.state.submitting ? 'disabled' : '') + '>' +
              (this.state.submitting ? 'Sending…' : 'Unlock my ' + esc(pct) + ' off') +
            '</button>' +
          '</div>' +
          '<p class="rwq-email-note">We\'ll email your code and your match. No spam, unsubscribe any time.</p>' +
        '</div>';

      return teaser + offer;
    }

    renderLegacyResults() {
      const p = (this.config.personas || {})[this.state.persona] || {};
      const stack = (p.stack || []).map(s =>
        '<li><strong>' + esc(s.title) + '</strong><span>' + esc(s.why) + '</span></li>').join('');
      const url = (p.url || this.config.defaultUrl || '#') +
        ((p.url || this.config.defaultUrl || '').indexOf('?') >= 0 ? '&' : '?') +
        (p.code ? 'discount=' + encodeURIComponent(p.code) + '&' : '') +
        'utm_source=quiz&utm_medium=pdp&utm_campaign=' + encodeURIComponent(this.config.product) +
        '&utm_content=' + encodeURIComponent(this.state.persona);
      return (
        '<span class="rwq-result-eyebrow">Your match</span>' +
        '<h3 class="rwq-title">' + esc(p.name || 'Your reset profile') + '</h3>' +
        (p.reasoning ? '<p class="rwq-hint">' + esc(p.reasoning) + '</p>' : '') +
        (stack ? '<ul class="rwq-stack">' + stack + '</ul>' : '') +
        '<div class="rwq-cta-row"><a class="rwq-cta" href="' + esc(url) + '">' + esc(p.cta || 'See the pack') + '</a></div>'
      );
    }

    buildReasoning() {
      const a = this.state.answers;
      const bits = [];
      if (a.goal) bits.push(this.labelFor('goal', a.goal));
      if (a.context) bits.push(this.labelFor('context', a.context));
      if (a.stage) bits.push(this.labelFor('stage', a.stage));
      if (Array.isArray(a.modifiers) && a.modifiers.length) {
        bits.push(a.modifiers.map(v => this.labelFor('modifiers', v)).filter(Boolean).join(' + '));
      }
      return bits.filter(Boolean).join(' · ').toLowerCase();
    }

    labelFor(qId, value) {
      const q = this.config.questions.find(x => x.id === qId);
      if (!q) return value;
      const opts = resolveOptions(q, this.state.answers);
      const o = opts.find(x => x.v === value);
      return o ? o.label : value;
    }

    withUtm(url, contentKey, code) {
      if (!url) return '#';
      const sep = url.indexOf('?') >= 0 ? '&' : '?';
      return url + sep + 'discount=' + encodeURIComponent(code) +
        '&utm_source=quiz&utm_medium=' + encodeURIComponent(this.mode === 'modal' ? 'popup' : 'pdp') +
        '&utm_campaign=' + encodeURIComponent(this.config.product) +
        '&utm_content=' + encodeURIComponent(contentKey);
    }

    renderThanks() {
      // Server-side blocked plan (under-18, pregnancy filter empty, red flag)
      if (this.state.serverBlocked) {
        return (
          '<div class="rwq-thanks-mark">!</div>' +
          '<h3 class="rwq-title">Let\'s pause here.</h3>' +
          '<p class="rwq-hint">' + esc(this.state.serverBlocked.message || 'Please speak with a healthcare professional first.') + '</p>'
        );
      }
      const primary = this.state.primary;
      const stack = this.state.stack;
      const code = this.config.defaultCode || 'n10';
      const pct  = this.config.rewardPct || '10%';
      const server = this.state.serverPlan;
      const fallbackUrl = primary
        ? this.withUtm(primary.product.url, primary.key, code)
        : (this.config.defaultUrl + '?discount=' + encodeURIComponent(code));
      // Prefer server-built cart URL (real variant IDs, selling plan attached when available)
      const url = (server && server.cart_url) ? server.cart_url : fallbackUrl;
      const label = (server && server.supplement_stack && server.supplement_stack.length > 1)
        ? ('Add your stack (' + server.supplement_stack.length + ')')
        : (primary ? ('Shop ' + primary.product.name) : 'Shop your match');
      const otpLink = (server && server.cart_url_otp && server.cart_url_otp !== url)
        ? '<div class="rwq-cta-row"><a class="rwq-cta rwq-cta-ghost" href="' + esc(server.cart_url_otp) + '">One-time purchase</a></div>'
        : '';
      const fallbackCartUrl = (primary && stack && !server)
        ? ('/cart/add?items[][id]=' + encodeURIComponent(primary.product.handle) +
           '&items[][id]=' + encodeURIComponent(stack.product.handle) +
           '&discount=' + encodeURIComponent(code))
        : null;
      const stackCta = (!server && stack)
        ? '<a class="rwq-cta rwq-cta-ghost" href="' + esc(fallbackCartUrl || this.withUtm(stack.product.url, stack.key, code)) + '">Add ' + esc(stack.product.name) + ' too</a>'
        : '';
      const notes = (server && server.safety_notes && server.safety_notes.length)
        ? '<p class="rwq-hint" style="margin-top:14px;font-size:12px;opacity:.85;">' + esc(server.safety_notes[0]) + '</p>'
        : '';
      return (
        '<div class="rwq-thanks-mark">✓</div>' +
        '<span class="rwq-pill">Unlocked</span>' +
        '<h3 class="rwq-title">Your <em>' + esc(pct) + ' off</em> is in your inbox.</h3>' +
        '<p class="rwq-hint">Code applies at checkout. Free 3-in-1 Magnesium pack lands automatically on orders over £25.</p>' +
        '<div class="rwq-code-reveal" data-rwq-copy="' + esc(code) + '">' +
          '<span class="rwq-code-value">' + esc(code) + '</span>' +
          '<button class="rwq-code-copy" data-rwq-copy-btn type="button">Copy</button>' +
        '</div>' +
        '<div class="rwq-cta-row"><a class="rwq-cta" href="' + esc(url) + '">' + esc(label) + '</a></div>' +
        (stackCta ? '<div class="rwq-cta-row">' + stackCta + '</div>' : '') +
        otpLink +
        notes
      );
    }

    bind() {
      const self = this;
      this.querySelectorAll('[data-rwq-pick]').forEach(b => {
        b.addEventListener('click', () => {
          const [q, v] = b.getAttribute('data-rwq-pick').split('|');
          self.pick(q, v);
        });
      });
      const nx = this.querySelector('[data-rwq-next]'); if (nx) nx.addEventListener('click', () => self.next());
      const em = this.querySelector('[data-rwq-email]'); if (em) em.addEventListener('input', e => { self.state.email = e.target.value; });
      const sb = this.querySelector('[data-rwq-submit]'); if (sb) sb.addEventListener('click', () => self.submitEmail());
      const ts = this.querySelector('[data-rwq-teaser]'); if (ts) ts.addEventListener('click', () => self.open('teaser'));
      const cl = this.querySelector('[data-rwq-close]'); if (cl) cl.addEventListener('click', () => self.close('x'));
      const bk = this.querySelector('[data-rwq-back]'); if (bk) bk.addEventListener('click', () => self.back());
      const bd = this.querySelector('[data-rwq-backdrop]'); if (bd) bd.addEventListener('click', () => self.close('backdrop'));
      const cp = this.querySelector('[data-rwq-copy-btn]');
      if (cp) cp.addEventListener('click', () => {
        const node = self.querySelector('[data-rwq-copy]');
        const code = node && node.getAttribute('data-rwq-copy');
        if (!code) return;
        try { navigator.clipboard.writeText(code); } catch (_) {}
        cp.textContent = 'Copied';
        setTimeout(() => { cp.textContent = 'Copy'; }, 1800);
      });
    }
  }

  customElements.define('roundway-quiz', RoundwayQuiz);
})();
